import { useState } from "react";
import { ValueLabel } from "@features/ax/shared/ValueLabel";
import { useCurrency } from "@shared/selectors/globalSelectors";
import { Badge } from "@shared/ui/badge";
import { Button } from "@shared/ui/button";
import { Card, CardContent, CardHeader } from "@shared/ui/card";
import { PageHeader } from "@shared/components/PageHeader";
import { getStatusBadgeClass } from "@shared/utils/statusUtils";
import { AlertTriangle, ArrowLeft, Bell, FileText, User } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { SendReminderDialog } from "./dialogueComponents/SendReminderDialog";
import { SendNoticeDialog } from "./dialogueComponents/SendNoticeDialog";

interface ConsumerDetails {
  id: string;
  name: string;
  accountNo: string;
  status: string;
  riskLevel: string;
  phone: string;
  email: string;
  address: string;
}

interface OutstandingDetails {
  totalOutstanding: number;
  pendingBills: number;
  lastPaymentAmount?: number;
  lastPaidDate?: string;
}

interface Props {
  consumer: ConsumerDetails;
  outstanding: OutstandingDetails;
  onViewProfile: () => void;
  onPayNow: () => void;
  onCreateAgreement: () => void;
}

export default function ConsumerOutstandingCard({
  consumer,
  outstanding,
  onViewProfile,
  onPayNow,
  onCreateAgreement,
}: Props) {
  const navigate = useNavigate();
  const currency = useCurrency();
  const [reminderOpen, setReminderOpen] = useState(false);
  const [noticeOpen, setNoticeOpen] = useState(false);

  // Build an Account-compatible object for the dialogs
  const accountForDialog: any = {
    id: consumer.id,
    accountNumber: consumer.accountNo,
    consumerNo: consumer.accountNo,
    customerName: consumer.name,
    firstName: consumer.name.split(" ")[0] ?? consumer.name,
    lastName: consumer.name.split(" ").slice(1).join(" ") ?? "",
    email: consumer.email,
    serviceAddress: consumer.address,
    outstandingBalance: outstanding.totalOutstanding,
    billData: {
      outstandingBalance: outstanding.totalOutstanding,
      daysSinceLastBill: 0,
      lastPaymentDate: outstanding.lastPaidDate ?? null,
      lastPayAmount: outstanding.lastPaymentAmount ?? 0,
    },
  };

  const consumerFields = [
    {
      label: "Account Status",
      value: (
        <Badge
          className={
            consumer.status?.toLowerCase() === "active"
              ? "bg-green-100 text-green-800 hover:bg-green-100 border-none mt-1"
              : "bg-red-100 text-gray-800 hover:bg-red-100 border-none mt-1"
          }
        >
          {consumer.status}
        </Badge>
      ),
    },
    { label: "Risk Level", value: consumer.riskLevel },
    { label: "Phone", value: consumer.phone },
    {
      label: "Email",
      value: (
        <span className="block max-w-[180px] truncate" title={consumer.email}>
          {consumer.email}
        </span>
      ),
    },
    {
      label: "Service Address",
      value: consumer.address,
      className: "col-span-2",
    },
  ];

  return (
    <>
      <div className="space-y-4">
        {/* Page Header with back arrow */}
        <div className="flex justify-between">
          <PageHeader
            title="Outstanding Details"
            icon={ArrowLeft}
            onIconClick={() => navigate(-1)}
            subtitle="Detailed overview of outstanding."
          />

          {/* Action Buttons */}
          <div className="flex gap-2">
            <Button variant="destructive" onClick={() => setReminderOpen(true)}>
              <Bell className="mr-1.5 h-4 w-4" />
              Send Reminder
            </Button>
            <Button variant="default" onClick={() => setNoticeOpen(true)}>
              <FileText className="mr-1.5 h-4 w-4" />
              Send Notice
            </Button>
          </div>
        </div>

        {/* Cards Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-6 gap-4">
          {/* Consumer Details Card */}
          <Card className="lg:col-span-3">
            <CardHeader className="pb-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                      <User className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        {consumer.name}
                      </h3>
                      <p className="text-sm text-gray-500">
                        Account No: {consumer.accountNo}
                      </p>
                    </div>
                  </div>
                </div>
                <Button onClick={onViewProfile} variant="outline" size="sm">
                  View Profile
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-x-8 gap-y-4">
                {consumerFields.map((field, index) => (
                  <div key={index} className={field.className || ""}>
                    {field.label === "Risk Level" ? (
                      <div className="space-y-1">
                        <p className="text-xs text-muted-foreground tracking-wide leading-none font-medium">
                          {field.label}
                        </p>
                        <div className="flex items-center gap-2">
                          <Badge
                            variant="default"
                            className={getStatusBadgeClass(consumer.riskLevel)}
                          >
                            {field.value}
                          </Badge>
                          {consumer.riskLevel.toLowerCase() === "high" && (
                            <AlertTriangle className="w-4 h-4 text-red-600" />
                          )}
                        </div>
                      </div>
                    ) : (
                      <ValueLabel label={field.label} value={field.value} />
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Outstanding Details Card */}
          <Card className="lg:col-span-3">
            <CardHeader>
              <h3 className="text-base font-semibold text-gray-900">
                Outstanding Details
              </h3>
            </CardHeader>
            <CardContent className="grid grid-cols-2 gap-4 text-sm">
              <div className="space-y-2">
                <div className="space-y-1 mb-6">
                  <div className="text-sm text-muted-foreground">
                    Total Outstanding
                  </div>
                  <div className="text-2xl font-semibold">
                    {currency} {outstanding?.totalOutstanding}
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-xs text-muted-foreground tracking-wide font-medium">
                    Pending Bills
                  </span>
                  <span className="text-sm font-medium text-foreground">
                    {outstanding?.pendingBills ?? "NA"}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-xs text-muted-foreground tracking-wide font-medium">
                    Last Payment
                  </span>
                  <span className="text-sm font-medium text-foreground">
                    {outstanding?.lastPaymentAmount
                      ? `${currency} ${outstanding.lastPaymentAmount}`
                      : "—"}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-xs text-muted-foreground tracking-wide font-medium">
                    Last Paid Date
                  </span>
                  <span className="text-sm font-medium text-foreground">
                    {outstanding?.lastPaidDate ?? "—"}
                  </span>
                </div>
              </div>

              <div className="col-span-2 flex gap-2 pt-2 mt-2">
                <Button size="sm" onClick={onPayNow} className="w-full">
                  Pay Now
                </Button>
                <Button
                  variant="outline"
                  className="w-full"
                  size="sm"
                  onClick={onCreateAgreement}
                >
                  Create Agreement
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Dialogs */}
      <SendReminderDialog
        isOpen={reminderOpen}
        onClose={() => setReminderOpen(false)}
        account={accountForDialog}
        outstandingAmount={outstanding.totalOutstanding}
      />
      <SendNoticeDialog
        isOpen={noticeOpen}
        onClose={() => setNoticeOpen(false)}
        account={accountForDialog}
        outstandingAmount={outstanding.totalOutstanding}
      />
    </>
  );
}
